import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icon, StateScreen } from '../ui';
import {
  setSubApplicationStatus, fetchSubApplicationFile, downloadSubApplicationFile,
  getApplyAgreements, fetchApplyAgreement, uploadApplyAgreement,
} from '../../api';
import { matchesSearch, noMatchText } from '../../lib/pageSearch';
import { formatMoney, formatMoneyCompact } from '../../lib/format';
import { SECTIONS, FILES, STATUSES, AGREEMENT_KINDS, MINIMUMS, checksOf, minimumOf, moneyOf } from '../../lib/subApplication';
import { REGIONS } from '../../lib/regions';
import { copyText } from '../../lib/copyText';
import { fmtDay } from '../../lib/subcontractors';
import { PrimaryButton, SecondaryButton, ErrorBanner } from './SubUi';
import Lift from './Lift';
import ScrollRail from './ScrollRail';
import DocViewer, { DocPreview } from './DocViewer';
import ConfirmDialog from '../ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';

/* The applications from subs who want to work with E3 (Duncan 2026-10-27): what came in from workwith.e3es.com, the
   newest first, with the region its business address falls in. The people given "Sub Applications" see them all --
   Jeff, Eric, James and the South Texas construction manager. Laid out again 2026-10-28 (Duncan: "use the space on the
   screen wisely ... maybe they don't need to be lines? Maybe they need to be boxes?"; his picks from a mockup):
   - every application is a card, as many across as fit, the same columns in every section: who and where, the same
     checks for each -- Insurance, Workers' comp, Agreements, Documents, License -- and its numbers in tiles: the insurance it carries, workers'
     comp, years in business, its average contract and bonding capacity (his ask), short ($2M) to fit; an amount under
     E3's minimum is red or amber with what E3 needs;
   - the applications are in sections on one page -- New, Reviewing, Approved, Declined -- no tabs (Duncan 2026-10-28:
     "get rid of the five tabs ... put them into sections on one page. Use that pencil scroll ... to signify which
     section you're currently scrolling through"): the pencil rail (ScrollRail.js, as on the punch list) beside them;
   - the form's link and its two PDFs sit behind a "The form" button over the cards;
   - a card lifts out to a review sheet that fills the window (Duncan 2026-10-28: "a lot of empty space with a bunch of
     information shoved up at the top ... make it user intuitive and friendly"; his picks from a mockup): who it is up
     top, a call or an email away; on the left the verdict -- does it meet E3's requirements, the checks spelled out --
     with Approve, Mark reviewing and Decline under it, the project it wants and its references; in the middle its
     insurance against what E3 needs (a table), its size in big numbers, its work and company; on the right its W-9
     and safety plan shown right there, filling the height. Bigger type throughout; full amounts with cents.
   A decision -- Approve, Mark reviewing, Decline (with why) -- only moves the application between the sections here;
   nothing of it leaves this page yet (Duncan 2026-10-28: "we're not ready to build out the rest of the system"). */

export const PUBLIC_URL = 'https://workwith.e3es.com';
const shell = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' };
const select = { height: 32, padding: '0 10px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-1)', fontSize: 13.5, fontFamily: 'inherit' };
const TONE = {
  new: { color: 'var(--accent-text)', bg: 'var(--accent-soft)' },
  reviewing: { color: 'var(--warning)', bg: 'var(--warning-soft)' },
  approved: { color: 'var(--success)', bg: 'var(--success-soft)' },
  declined: { color: 'var(--text-3)', bg: 'var(--bg-surface-2)' },
  warn: { color: 'var(--warning)', bg: 'var(--warning-soft)' },
  bad: { color: 'var(--danger)', bg: 'var(--danger-soft)' },
};
const STATUS_LABEL = Object.fromEntries(STATUSES);
const pill = (tone) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
  color: TONE[tone].color, background: TONE[tone].bg, border: `1px solid color-mix(in oklch, ${TONE[tone].color} 20%, transparent)`,
});
const contactLink = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)', color: 'var(--text-2)', fontSize: 13, fontWeight: 500, textDecoration: 'none', whiteSpace: 'nowrap',
};
// A moment's day where the reader is (an evening upload in Texas is already tomorrow in UTC).
const dayOf = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const ago = (iso) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : d < 7 ? `${d} days ago` : fmtDay(dayOf(iso));
};
const placeOf = (a) => [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
const telOf = (phone) => `tel:${String(phone).replace(/[^\d+]/g, '')}`;
const DOC_NAME = { w9: 'W-9', safety: 'Safety plan' };
// Amounts: short on a card ($2M, $500k -- Summit's compact money, its .00 dropped), in full with cents once opened.
const short$ = (v) => { const n = moneyOf(v); return n == null ? null : formatMoneyCompact(n).replace(/\.00M$/, 'M').replace(/(\.\d)0M$/, '$1M'); };
const full$ = (v) => { const n = moneyOf(v); return n == null ? null : formatMoney(n); };
// The dollar figures in a typed answer (bonding capacity), shortened for a card.
const shortIn = (text) => String(text).replace(/\$\s?(\d[\d,]*(?:\.\d+)?)/g, (m, num) => short$(num) || m);
const yesNo = (v) => (v === 'yes' ? 'Yes' : v === 'no' ? 'No' : null);
const has = (v) => v != null && String(v).trim() !== '';
const FIELD_OF = Object.fromEntries(SECTIONS.flatMap((s) => (s.fields || []).map((f) => [f.key, f])));
const INSURANCE = ['gl_coverage', 'auto_coverage', 'umbrella_coverage'];
// A card's tiles are narrow: their insurance under the trade's own short names.
const CARD_LABEL = { gl_coverage: 'GL', auto_coverage: 'Auto', umbrella_coverage: 'Umbrella' };
const SIZE = ['years_in_business', 'employees', 'average_contract', 'largest_contract', 'smallest_contract', 'backlog'];
// The review sheet's answer lists (the contact, phone, email and trades are in its header; the usual work reads as a
// paragraph of its own).
const COMPANY = ['address_line1', 'website', 'license_no'];
const WORK = ['subcontracted_trades', 'labor_outsourced', 'travel', 'union', 'hub'];
// An application's W-9 or safety plan, read for the sheet's preview (a doc made once per file: see `previewDoc`).
const fetchAppFile = (d) => fetchSubApplicationFile(d.appId, d.kind);
const VERDICT = {
  ok: { icon: 'check', color: 'var(--success)' },
  warn: { icon: 'alert-triangle', color: 'var(--warning)' },
  bad: { icon: 'x', color: 'var(--danger)' },
};

const CHECK_ICON = { ok: 'check', warn: 'alert-triangle', bad: 'x' };
/** One of the checks: quiet with a green tick when it's fine, tinted when it isn't; what it found on hover. */
const CheckChip = ({ check }) => (
  <span title={check.lines.join(' ')} style={check.tone === 'ok'
    ? { ...pill('declined'), color: 'var(--text-3)', background: 'transparent', border: '1px solid var(--border-default)' }
    : pill(check.tone)}>
    <span style={{ display: 'inline-flex', color: check.tone === 'ok' ? 'var(--success)' : 'inherit' }}>
      <Icon name={CHECK_ICON[check.tone]} size={12} stroke={2.4} />
    </span>
    {check.label}
  </span>
);

/** A number in a tile: its label, the value (a dash when there's none), red or amber when it's a problem, and a note. */
const Tile = ({ label, value, tone, note, span, title }) => (
  <div className={`app-tile${tone ? ` is-${tone}` : ''}`} style={span ? { gridColumn: `span ${span}` } : undefined} title={title}>
    <div className="app-tile-k">{label}</div>
    <div className="app-tile-v">{has(value) ? value : <span className="app-none">—</span>}</div>
    {note && <div className="app-tile-n">{note}</div>}
  </div>
);

const Styles = () => (
  <style>{`
    .app-main:has(.apps-page) { overflow-y: visible; overflow-x: hidden; overflow-x: clip; }
    .apps-sec { display: flex; align-items: center; gap: 8px; margin: 0 0 10px; font-size: 14.5px; font-weight: 650; color: var(--text-1); }
    .app-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 420px), 1fr)); gap: 14px; }
    .app-card { container-type: inline-size; display: flex; flex-direction: column; gap: 10px; min-width: 0; max-width: 760px; padding: 14px 16px; background: var(--bg-surface); border: 1px solid var(--border-default);
      border-radius: 12px; box-shadow: var(--shadow-sm); cursor: pointer; transition: border-color .15s, box-shadow .15s; }
    .app-card:hover { border-color: var(--border-strong); box-shadow: var(--shadow-md); }
    .app-card:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--accent-ring); }
    .app-tiles { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
    @container (min-width: 660px) { .app-tiles { grid-template-columns: repeat(6, minmax(0, 1fr)); } }
    .app-tile { min-width: 0; padding: 7px 10px; border-radius: 8px; background: var(--bg-surface-2); }
    .app-tile-k { font-size: 11px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .app-tile-v { margin-top: 1px; font-size: 14px; font-weight: 650; color: var(--text-1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .app-tile-n { font-size: 11px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .app-tile.is-warn { background: var(--warning-soft); } .app-tile.is-warn .app-tile-v { color: var(--warning); }
    .app-tile.is-bad { background: var(--danger-soft); } .app-tile.is-bad .app-tile-v { color: var(--danger); }
    .app-none { color: var(--text-4); font-weight: 500; }
    .app-answers { margin: 0; display: grid; grid-template-columns: minmax(110px, 170px) minmax(0, 1fr); gap: 8px 14px; font-size: 14px; }
    .app-answers dt { color: var(--text-3); }
    .app-answers dd { margin: 0; color: var(--text-1); font-weight: 500; overflow-wrap: anywhere; }
    .app-contact:hover { background: var(--bg-hover); }
    .sheet-c { container-type: inline-size; padding: 18px 22px 28px; }
    .sheet { display: grid; gap: 16px; grid-template-columns: minmax(0, 1fr); grid-template-areas: "left" "mid" "docs"; }
    @container (min-width: 900px) { .sheet { grid-template-columns: 340px minmax(0, 1fr); grid-template-areas: "left mid" "left docs"; align-items: start; } }
    @container (min-width: 1500px) { .sheet { grid-template-columns: 360px minmax(0, 1fr) minmax(460px, 0.9fr); grid-template-areas: "left mid docs"; } }
    .sheet-left, .sheet-mid { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
    .sheet-left { grid-area: left; } .sheet-mid { grid-area: mid; } .sheet-docs { grid-area: docs; }
    .sheet-card { min-width: 0; padding: 16px 18px; background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 14px; }
    .sheet-title { margin-bottom: 12px; font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--text-3); }
    .sheet-title span { margin-left: 6px; font-weight: 500; letter-spacing: 0; text-transform: none; }
    .sheet-verdict { display: flex; align-items: center; gap: 10px; font-size: 18px; font-weight: 700; letter-spacing: -0.01em; }
    .sheet-verdict-i { flex: none; width: 34px; height: 34px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; }
    .sheet-check { display: flex; gap: 12px; align-items: flex-start; padding: 11px 0; border-top: 1px solid var(--border-subtle); }
    .sheet-check-label { font-size: 14.5px; font-weight: 650; color: var(--text-1); }
    .sheet-check-line { margin-top: 1px; font-size: 13.5px; line-height: 1.45; color: var(--text-3); }
    .sheet-big { font-size: 16px; font-weight: 600; line-height: 1.4; color: var(--text-1); }
    .sheet-ins { width: 100%; border-collapse: collapse; font-size: 14.5px; }
    /* (App.css styles every th and td -- a white header, light borders, a hover -- and a Lift sits outside .app's dark theme) */
    .sheet-ins th { padding: 0 10px 8px 0; text-align: left; font-size: 12px; font-weight: 600; color: var(--text-3); background: transparent; border-bottom: none; }
    .sheet-ins td { padding: 10px 10px 10px 0; border-top: 1px solid var(--border-subtle); border-bottom: none; color: var(--text-1); font-weight: 500; font-variant-numeric: tabular-nums; }
    .sheet-ins tbody tr:hover { background: transparent; }
    .sheet-ins td:first-child { color: var(--text-2); font-weight: 400; }
    .sheet-ins td.is-warn { color: var(--warning); font-weight: 650; } .sheet-ins td.is-bad { color: var(--danger); font-weight: 650; }
    .sheet-mid { container-type: inline-size; }
    .sheet-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
    @container (max-width: 640px) { .sheet-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    .sheet-stat { min-width: 0; padding: 10px 12px; border-radius: 10px; background: var(--bg-surface-2); }
    .sheet-stat-k { font-size: 12px; color: var(--text-3); }
    .sheet-stat-v { margin-top: 2px; font-size: 20px; font-weight: 700; letter-spacing: -0.01em; color: var(--text-1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-variant-numeric: tabular-nums; }
    .sheet-pair { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 14px; align-items: start; }
    .sheet-docs { display: flex; flex-direction: column; gap: 10px; height: 560px; }
    @container (min-width: 900px) { .sheet-docs { height: 680px; } }
    @container (min-width: 1500px) { .sheet-docs { height: max(560px, calc(100vh - 170px)); } }
    .sheet-doc-view { flex: 1; min-height: 0; }
    .sheet-doc-tab { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 34px; border-radius: 8px; cursor: pointer;
      font: 500 13.5px var(--font-sans); color: var(--text-2); background: var(--bg-surface); border: 1px solid var(--border-default); }
    .sheet-doc-tab[aria-pressed="true"] { color: var(--text-1); font-weight: 650; background: var(--bg-surface-2); border-color: var(--border-strong); }
    .sheet-trade { padding: 3px 10px; border-radius: 999px; font-size: 12.5px; color: var(--text-2); background: var(--bg-surface-2); border: 1px solid var(--border-default); }
    @media (max-width: 560px) {
      .sheet-c { padding: 12px; }
      .app-answers { grid-template-columns: minmax(0, 1fr); gap: 2px; }
      .app-answers dd { margin-bottom: 6px; }
    }
  `}</style>
);

/** The form's link and the two agreements it links (Duncan 2026-10-27: "we need to have the spot in the application mgr
    for the 2 pdfs that can be replaced as needed"), opened from "The form" over the cards: copy the link; each agreement
    on file -- its name, who put it there and when -- to View or Replace, or Upload while there's none (its question
    stays off the form till then). They're public, so blank copies only. */
function FormPanel({ docs, docsError, onChanged, copied, onCopy }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null); // { kind, doc }
  const pickers = useRef({});
  const { confirm, confirmDialogProps } = useConfirm();

  const put = async (kind, label, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) { setError(`${file.name} isn’t a PDF: the form links PDFs`); return; }
    if (file.size > 20 * 1024 * 1024) { setError(`${file.name} is over 20 MB`); return; }
    const had = docs && docs[kind];
    if (had) {
      const ok = await confirm({
        title: `Replace the ${label}?`,
        message: `${file.name} takes the place of ${had.filename} on the form at workwith.e3es.com, for anyone who opens it from now on.`,
        confirmLabel: 'Replace',
      });
      if (!ok) return;
    }
    setBusy(kind);
    setError(null);
    try {
      await uploadApplyAgreement(kind, file);
      await onChanged();
    } catch (e) {
      setError(e.message || 'The upload didn’t go through');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div aria-label="The form" style={{ padding: '14px 16px', background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border-default)', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, color: 'var(--text-2)' }}>Subs apply at <span className="mono" style={{ color: 'var(--text-1)' }}>workwith.e3es.com</span></span>
        <SecondaryButton onClick={onCopy} style={{ height: 30, fontSize: 13 }}>
          <Icon name={copied === 'yes' ? 'check' : 'template'} size={14} />{copied === 'yes' ? 'Copied' : copied === 'no' ? 'Couldn’t copy' : 'Copy the link'}
        </SecondaryButton>
      </div>
      {(error || docsError) && <ErrorBanner>{error || docsError}</ErrorBanner>}
      {/* Each PDF a compact card, side by side from the left (Duncan 2026-10-28: stretched across the page they were
          "spread out really far from left or right"). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 400px), 580px))', gap: 10 }}>
        {AGREEMENT_KINDS.map(([kind, label]) => {
          const d = docs && docs[kind];
          return (
            <div key={kind} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-default)', background: 'var(--bg-surface)' }}>
              <span aria-hidden="true" className="mono" style={{
                minWidth: 34, height: 24, padding: '0 5px', borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10.5, fontWeight: 700, color: '#fff', background: d ? 'var(--danger)' : 'var(--text-4)',
              }}>PDF</span>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 650, color: 'var(--text-1)' }}>{label}</div>
                {d ? (
                  <>
                    <div style={{ fontSize: 12.5, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={d.filename}>{d.filename}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{[d.uploaded_by_name, fmtDay(dayOf(d.uploaded_at))].filter(Boolean).join(' · ')}</div>
                  </>
                ) : (
                  <div style={{ fontSize: 12.5, color: 'var(--warning)', fontWeight: 600 }}>None yet: its question stays off the form till there is</div>
                )}
              </div>
              {d && (
                <SecondaryButton onClick={() => setViewing({ kind, doc: { id: `agreement-${kind}-${d.uploaded_at}`, filename: d.filename } })} style={{ height: 30, fontSize: 13 }}>
                  <Icon name="eye" size={14} />View
                </SecondaryButton>
              )}
              <SecondaryButton busy={busy === kind} onClick={() => pickers.current[kind] && pickers.current[kind].click()} style={{ height: 30, fontSize: 13 }}>
                <Icon name="upload" size={14} />{busy === kind ? 'Uploading…' : d ? 'Replace' : 'Upload'}
              </SecondaryButton>
              <input ref={(el) => { pickers.current[kind] = el; }} type="file" accept="application/pdf,.pdf" hidden
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; put(kind, label, f); }} />
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Anyone can open these from the form: upload blank copies, never one filled in for a sub.</div>
      {viewing && (
        <DocViewer doc={viewing.doc} subtitle={`${Object.fromEntries(AGREEMENT_KINDS)[viewing.kind]} · on the form`}
          fetchBlob={() => fetchApplyAgreement(viewing.kind)}
          onDownload={() => fetchApplyAgreement(viewing.kind).then((b) => {
            const url = URL.createObjectURL(b);
            const a = document.createElement('a');
            a.href = url; a.download = viewing.doc.filename; document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
          })}
          onClose={() => setViewing(null)} />
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}

export default function ApplicationsView({ apps, onChanged, search }) {
  const [region, setRegion] = useState('');
  const [lift, setLift] = useState(null); // { id, rect, from }
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [declining, setDeclining] = useState(null); // { id, note }
  const [viewing, setViewing] = useState(null); // { app, kind, doc }: a file open in the viewer
  const [docKind, setDocKind] = useState('w9'); // the file the open application's sheet shows
  const [copied, setCopied] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [docs, setDocs] = useState(null); // the agreements on the form
  const [docsError, setDocsError] = useState(null);
  const { confirm, confirmDialogProps } = useConfirm();

  const loadDocs = useCallback(() => getApplyAgreements()
    .then((d) => { setDocs(d || {}); setDocsError(null); })
    .catch((e) => setDocsError(e.message || 'Couldn’t read the form’s PDFs')), []);
  useEffect(() => { loadDocs(); }, [loadDocs]);
  const missingDocs = docs ? AGREEMENT_KINDS.filter(([k]) => !docs[k]).length : 0;

  const list = apps || [];
  const shown = list
    .filter((a) => !region || a.region === region)
    .filter((a) => matchesSearch(search, a.firm_name, a.contact_name, a.city, a.region, a.project_interest, (a.trades || []).join(' ')))
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  const lifted = lift ? list.find((a) => a.id === lift.id) : null;
  // The file the sheet shows, made once per file so a list read again doesn't fetch it again.
  const shownFile = lifted && lifted.files && lifted.files[docKind];
  const previewKey = shownFile ? `${lifted.id}:${docKind}:${shownFile.filename}` : '';
  const previewDoc = useMemo(() => (previewKey ? { id: previewKey, filename: shownFile.filename, appId: lifted.id, kind: docKind } : null), [previewKey]);
  // The sections, in the order an application moves through them; each newest first.
  const bySection = Object.fromEntries(STATUSES.map(([k]) => [k, shown.filter((a) => a.status === k)]));

  // The pencil rail: where each section starts down the page, measured as the page lays out and whenever it changes
  // size (the punch list's page rail). The top bar stays at the top of the window: the rail starts under it, a jump
  // lands under it.
  const pageRef = useRef(null);
  const [rail, setRail] = useState({ on: false, items: [], head: 0 });
  const railKey = useRef(JSON.stringify(rail));
  const railCounts = useRef({});
  railCounts.current = Object.fromEntries(STATUSES.map(([k]) => [k, bySection[k].length]));
  useLayoutEffect(() => {
    const measure = () => {
      const headEl = document.querySelector('.main-content > header');
      const head = headEl ? Math.round(headEl.getBoundingClientRect().height) : 0;
      const items = STATUSES.map(([key, label]) => {
        const el = document.getElementById(`apps-${key}`);
        return el ? { id: key, label, count: railCounts.current[key], top: Math.round(el.getBoundingClientRect().top + window.scrollY - head - 12) } : null;
      }).filter(Boolean);
      const on = window.innerWidth >= 900 && document.documentElement.scrollHeight > window.innerHeight + 40 && items.length > 0;
      const next = { on, items, head };
      const key = JSON.stringify(next);
      if (key === railKey.current) return;
      railKey.current = key;
      setRail(next);
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (ro && pageRef.current) ro.observe(pageRef.current);
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  });

  // A card lifts out across the cards' full width (a card alone is too narrow for the application's boxes).
  const open = (a, el) => {
    const box = el.closest('[data-app]');
    const r = box.getBoundingClientRect();
    const g = box.parentElement.getBoundingClientRect();
    setError(null);
    setDeclining(null);
    setDocKind(a.files && !a.files.w9 && a.files.safety ? 'safety' : 'w9');
    setLift({ id: a.id, rect: { left: g.left, top: r.top, width: g.width, height: r.height }, from: box });
  };
  const copyLink = async () => setCopied((await copyText(PUBLIC_URL)) ? 'yes' : 'no');
  // An action, then the list read again; `after` once it went.
  const run = async (key, fn, after) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await onChanged();
      if (after) after();
    } catch (e) {
      setError(e.message || 'That didn’t go through');
    } finally {
      setBusy(null);
    }
  };
  // A decision only moves the application between this page's sections (Duncan 2026-10-28: "information doesn't leave
  // that tab for now"), and any of them can be reopened.
  const approve = async (a) => {
    const ok = await confirm({
      title: `Approve ${a.firm_name}?`,
      message: `${a.firm_name} moves to Approved on this page. Nothing goes to Subcontractor Mgmt yet, and it can be reopened.`,
      confirmLabel: 'Approve',
    });
    if (ok) await run('approve', () => setSubApplicationStatus(a.id, 'approved'));
  };

  // The tiles of an application's numbers: its insurance against E3's minimums, short on a card, in full once opened.
  const insuranceTiles = (a, hot, money, labels = {}) => {
    const ans = a.answers || {};
    return INSURANCE.map((k) => (
      <Tile key={k} label={labels[k] || FIELD_OF[k].brief} value={money(ans[k])} tone={hot[k]}
        note={hot[k] && has(ans[k]) ? `needs ${money(minimumOf(k, ans))}` : null} />
    ));
  };

  // A card: who and where, the four checks, its numbers, the project it wants.
  const appCard = (a) => {
    const ans = a.answers || {};
    const checks = checksOf(a);
    const hot = Object.assign({}, ...checks.map((c) => c.fields));
    return (
      <div key={a.id} data-app={a.id} className="app-card" role="button" tabIndex={0} aria-haspopup="dialog" aria-label={`Open ${a.firm_name}’s application`}
        onClick={(e) => open(a, e.currentTarget)} onKeyDown={(e) => { if (e.key === 'Enter') open(a, e.currentTarget); }}
        style={{ visibility: lift && lift.id === a.id ? 'hidden' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.firm_name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {[(a.trades || []).join(', ') || 'No trade given', placeOf(a)].filter(Boolean).join(' · ')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: 'var(--text-3)' }}>
              <Icon name="map-pin" size={12} />{a.region || 'No region'} · {ago(a.created_at)}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>{checks.map((c) => <CheckChip key={c.key} check={c} />)}</div>
        <div className="app-tiles">
          {insuranceTiles(a, hot, short$, CARD_LABEL)}
          <Tile label="Workers’ comp" value={yesNo(ans.workers_comp)} tone={hot.workers_comp} />
          <Tile label="Years" value={ans.years_in_business} title="Years in business" />
          <Tile label="Avg contract" value={short$(ans.average_contract)} />
        </div>
        {/* bonding is typed, so it reads on a line of its own */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 'auto', fontSize: 13, color: 'var(--text-3)' }}>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={has(ans.bonding) ? ans.bonding : undefined}>
            Bonding {has(ans.bonding) ? <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{shortIn(ans.bonding)}</span> : <span className="app-none">—</span>}
          </div>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={ans.project_interest}>
            Wants <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{ans.project_interest || a.project_interest || 'no project named'}</span>
          </div>
        </div>
      </div>
    );
  };

  // A lifted application's header: who it is, where, its trades, and a call or an email away.
  const head = (a) => {
    const ans = a.answers || {};
    const phone = ans.phone || a.phone;
    const email = ans.email || a.email;
    const big = { ...contactLink, height: 36, fontSize: 14 };
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 56px 16px 22px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 24, fontWeight: 750, letterSpacing: '-0.02em', color: 'var(--text-1)' }}>{a.firm_name}</span>
            <span style={pill(a.status)}>{STATUS_LABEL[a.status]}</span>
          </div>
          <div style={{ marginTop: 2, fontSize: 14, color: 'var(--text-3)' }}>
            {[a.contact_name, placeOf(a), a.region, `applied ${ago(a.created_at)}`, `#${a.id}`].filter(Boolean).join(' · ')}
          </div>
          {(a.trades || []).length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>{a.trades.map((t) => <span key={t} className="sheet-trade">{t}</span>)}</div>
          )}
        </div>
        {phone && <a href={telOf(phone)} className="app-contact" style={big}><Icon name="phone" size={15} />{phone}</a>}
        {email && <a href={`mailto:${email}`} className="app-contact" style={big}><Icon name="mail" size={15} />Email</a>}
      </div>
    );
  };

  // Answers under their short labels; the unanswered ones on a line at the foot.
  const answerList = (keys, a) => {
    const ans = a.answers || {};
    const rows = [];
    const unanswered = [];
    keys.forEach((k) => {
      const f = FIELD_OF[k];
      const v = ans[k];
      const text = k === 'address_line1' ? [ans.address_line1, placeOf(a)].filter(Boolean).join(', ')
        : Array.isArray(v) ? v.join(', ') : f.kind === 'yesno' ? yesNo(v) : has(v) ? String(v) : null;
      if (has(text)) rows.push([f, text]);
      else unanswered.push(f.brief);
    });
    return (
      <>
        {rows.length > 0 && (
          <dl className="app-answers">
            {rows.map(([f, text]) => <React.Fragment key={f.key}><dt>{f.brief}</dt><dd>{text}</dd></React.Fragment>)}
          </dl>
        )}
        {unanswered.length > 0 && <div style={{ marginTop: rows.length ? 10 : 0, fontSize: 13, color: 'var(--text-3)', lineHeight: 1.5 }}>Not answered: {unanswered.join(' · ')}</div>}
      </>
    );
  };

  // ...and the review sheet under it: the verdict and the decision, its numbers, its documents.
  const detail = (a) => {
    const checks = checksOf(a);
    const hot = Object.assign({}, ...checks.map((c) => c.fields));
    const ans = a.answers || {};
    const worst = checks.some((c) => c.tone === 'bad') ? 'bad' : checks.some((c) => c.tone === 'warn') ? 'warn' : 'ok';
    const open = checks.filter((c) => c.tone !== 'ok').length;
    const verdict = worst === 'ok' ? 'Meets E3’s requirements' : worst === 'bad' ? 'Doesn’t meet E3’s requirements' : `${open} thing${open === 1 ? '' : 's'} to sort out`;
    const undecided = a.status === 'new' || a.status === 'reviewing';
    const by = a.reviewed_by_name || 'E3';
    const on = a.reviewed_at ? fmtDay(dayOf(a.reviewed_at)) : null;
    const refs = (ans.references || []).filter((r) => r && (r.company || r.contact || r.phone));
    const wide = { width: '100%', height: 38 };
    const file = a.files && a.files[docKind];
    const meets = (tone) => <span style={{ display: 'inline-flex', color: VERDICT[tone].color }}><Icon name={VERDICT[tone].icon} size={16} stroke={2.4} /></span>;

    return (
      <div className="sheet-c">
        <div className="sheet">
          <div className="sheet-left">
            <section aria-label="Verdict" className="sheet-card" style={{ borderColor: `color-mix(in oklch, ${VERDICT[worst].color} 35%, var(--border-default))` }}>
              <div className="sheet-verdict" style={{ color: VERDICT[worst].color }}>
                <span className="sheet-verdict-i" style={{ background: `color-mix(in oklch, ${VERDICT[worst].color} 14%, transparent)` }}><Icon name={VERDICT[worst].icon} size={18} stroke={2.6} /></span>
                {verdict}
              </div>
              <div style={{ marginTop: 10 }}>
                {checks.map((c) => (
                  <div key={c.key} className="sheet-check">
                    <span style={{ marginTop: 2 }}>{meets(c.tone)}</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="sheet-check-label">{c.label}</div>
                      <div className="sheet-check-line" style={c.tone === 'ok' ? undefined : { color: 'var(--text-1)' }}>{c.lines.join(' ')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
            <section aria-label="Decision" className="sheet-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {error && <ErrorBanner>{error}</ErrorBanner>}
              <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>
                {a.status === 'new' ? 'Not decided yet' : `${STATUS_LABEL[a.status]} by ${by}${on ? `${a.status === 'reviewing' ? ' since' : ' on'} ${on}` : ''}`}
              </div>
              {a.status === 'declined' && a.decision_note && <div style={{ fontSize: 14, color: 'var(--text-1)', lineHeight: 1.45 }}>{a.decision_note}</div>}
              {undecided ? (
                declining && declining.id === a.id ? (
                  <>
                    <textarea autoFocus value={declining.note} onChange={(e) => setDeclining({ id: a.id, note: e.target.value })} placeholder="Why (only E3 sees this)"
                      rows={3} maxLength={500} style={{ ...select, height: 'auto', padding: '8px 10px', resize: 'vertical', lineHeight: 1.4, fontSize: 14 }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <SecondaryButton onClick={() => setDeclining(null)} style={{ flex: 1 }}>Cancel</SecondaryButton>
                      <SecondaryButton danger busy={busy === 'decline'} style={{ flex: 1 }}
                        onClick={() => run('decline', () => setSubApplicationStatus(a.id, 'declined', declining.note.trim()), () => setDeclining(null))}>Decline</SecondaryButton>
                    </div>
                  </>
                ) : (
                  <>
                    <PrimaryButton busy={busy === 'approve'} onClick={() => approve(a)} style={{ ...wide, height: 42, fontSize: 15 }}><Icon name="check" size={16} stroke={2.2} />Approve</PrimaryButton>
                    {a.status === 'new' && <SecondaryButton busy={busy === 'reviewing'} onClick={() => run('reviewing', () => setSubApplicationStatus(a.id, 'reviewing'))} style={wide}>Mark reviewing</SecondaryButton>}
                    <SecondaryButton danger onClick={() => setDeclining({ id: a.id, note: '' })} style={wide}>Decline…</SecondaryButton>
                  </>
                )
              ) : (
                <>
                  <SecondaryButton busy={busy === 'reopen'} onClick={() => run('reopen', () => setSubApplicationStatus(a.id, 'reviewing'))} style={wide}>Reopen: back to Reviewing</SecondaryButton>
                </>
              )}
            </section>
            <section aria-label="Wants to work on" className="sheet-card">
              <div className="sheet-title">Wants to work on</div>
              <div className="sheet-big" style={ans.project_interest ? undefined : { color: 'var(--text-4)' }}>{ans.project_interest || 'Didn’t say'}</div>
            </section>
            <section aria-label="References" className="sheet-card">
              <div className="sheet-title">References</div>
              {refs.length ? refs.map((r, i) => (
                <div key={i} style={{ padding: '8px 0', borderTop: i ? '1px solid var(--border-subtle)' : 'none', fontSize: 14 }}>
                  <div style={{ fontWeight: 650, color: 'var(--text-1)' }}>{r.company || '—'}</div>
                  <div style={{ color: 'var(--text-3)' }}>
                    {r.contact}{r.contact && r.phone ? ' · ' : ''}{r.phone && <a href={telOf(r.phone)} style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>{r.phone}</a>}
                  </div>
                </div>
              )) : <div style={{ fontSize: 14, color: 'var(--text-3)' }}>None given</div>}
            </section>
          </div>

          <div className="sheet-mid">
            <section aria-label="Insurance" className="sheet-card">
              <div className="sheet-title">Insurance<span>what they carry against what E3 needs</span></div>
              <table className="sheet-ins">
                <thead><tr><th>Coverage</th><th>They carry</th><th>E3 needs</th><th aria-label="Meets it" style={{ width: 24 }} /></tr></thead>
                <tbody>
                  {INSURANCE.map((k) => {
                    const v = moneyOf(ans[k]);
                    const need = minimumOf(k, ans);
                    const tone = v != null && v >= need ? 'ok' : hot[k] || 'warn';
                    return (
                      <tr key={k}>
                        <td>{FIELD_OF[k].brief}</td>
                        <td className={tone === 'ok' ? undefined : `is-${tone}`}>{full$(ans[k]) || 'Not given'}</td>
                        <td>{formatMoney(need)}{need > MINIMUMS[k] ? ' for roofing' : ''}</td>
                        <td>{meets(tone)}</td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td>Workers’ comp</td>
                    <td className={ans.workers_comp === 'yes' ? undefined : hot.workers_comp ? 'is-bad' : 'is-warn'}>{ans.workers_comp === 'yes' ? 'Full' : ans.workers_comp === 'no' ? 'Not full' : 'Didn’t say'}</td>
                    <td>Full</td>
                    <td>{meets(ans.workers_comp === 'yes' ? 'ok' : ans.workers_comp === 'no' ? 'bad' : 'warn')}</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: 10, fontSize: 14, color: 'var(--text-3)' }}>
                Will raise coverage to E3’s minimum:{' '}
                <span style={{ fontWeight: 650, color: hot.increase_coverage ? 'var(--danger)' : 'var(--text-1)' }}>{yesNo(ans.increase_coverage) || 'Didn’t say'}</span>
              </div>
            </section>
            <section aria-label="Size and capacity" className="sheet-card">
              <div className="sheet-title">Size and capacity</div>
              <div className="sheet-stats">
                {SIZE.map((k) => {
                  const f = FIELD_OF[k];
                  const v = f.kind === 'money' ? full$(ans[k]) : ans[k];
                  return (
                    <div key={k} className="sheet-stat">
                      <div className="sheet-stat-k">{f.brief}</div>
                      <div className="sheet-stat-v" title={has(v) ? String(v) : undefined}>{has(v) ? v : <span className="app-none">—</span>}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 12, fontSize: 14, color: 'var(--text-3)' }}>
                {FIELD_OF.bonding.brief}: <span style={{ fontWeight: 600, color: has(ans.bonding) ? 'var(--text-1)' : 'var(--text-4)' }}>{has(ans.bonding) ? ans.bonding : 'Not given'}</span>
              </div>
            </section>
            <div className="sheet-pair">
              <section aria-label="Their work" className="sheet-card">
                <div className="sheet-title">Their work</div>
                {has(ans.scope) && <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.5, color: 'var(--text-1)' }}>{ans.scope}</p>}
                {answerList(WORK, a)}
              </section>
              <section aria-label="Company" className="sheet-card">
                <div className="sheet-title">Company</div>
                {answerList(COMPANY, a)}
              </section>
            </div>
          </div>

          <section aria-label="Documents" className="sheet-card sheet-docs">
            <div style={{ display: 'flex', gap: 8 }} role="group" aria-label="Which document">
              {FILES.map((f) => {
                const sent = a.files && a.files[f.key];
                return (
                  <button key={f.key} type="button" className="sheet-doc-tab" aria-pressed={docKind === f.key} onClick={() => setDocKind(f.key)}>
                    <span style={{ display: 'inline-flex', color: sent ? 'var(--success)' : 'var(--warning)' }}><Icon name={sent ? 'check' : 'alert-triangle'} size={14} stroke={2.2} /></span>
                    {DOC_NAME[f.key]}
                  </button>
                );
              })}
            </div>
            <div className="sheet-doc-view">
              {file && previewDoc ? <DocPreview doc={previewDoc} fetchBlob={fetchAppFile} /> : (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 10, border: '1.5px dashed var(--border-strong)', color: 'var(--text-3)', fontSize: 14 }}>
                  <Icon name="alert-triangle" size={22} />
                  No {docKind === 'w9' ? 'W-9' : 'safety plan'} came with the application
                </div>
              )}
            </div>
            {file && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={file.filename}>{file.filename}</span>
                <SecondaryButton onClick={() => setViewing({ app: a, kind: docKind, doc: { id: `${a.id}-${docKind}`, filename: file.filename } })} style={{ height: 32, fontSize: 13 }}>
                  <Icon name="eye" size={14} />Full screen
                </SecondaryButton>
                <SecondaryButton onClick={() => downloadSubApplicationFile(a.id, docKind, file.filename).catch((e) => setError(e.message || 'Download failed'))} style={{ height: 32, fontSize: 13 }}>
                  <Icon name="download" size={14} />Download
                </SecondaryButton>
              </div>
            )}
          </section>
        </div>
      </div>
    );
  };

  return (
    <>
      <Styles />
      <section style={{ ...shell, overflow: 'hidden' }} aria-label="Filter the applications">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 14px' }}>
          <select value={region} onChange={(e) => setRegion(e.target.value)} aria-label="Region" style={select}>
            <option value="">All regions</option>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          <SecondaryButton aria-expanded={formOpen} onClick={() => setFormOpen((v) => !v)} style={{ height: 32, fontSize: 13 }}
            title={missingDocs ? `${missingDocs === 1 ? 'A PDF isn’t' : 'The PDFs aren’t'} on the form yet` : undefined}>
            <Icon name="share" size={14} />The form: link and PDFs
            {missingDocs > 0 && <span aria-label="A PDF is missing" style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--warning)' }} />}
            <Icon name={formOpen ? 'chevron-up' : 'caret'} size={14} />
          </SecondaryButton>
        </div>
        {formOpen && <FormPanel docs={docs} docsError={docsError} onChanged={loadDocs} copied={copied} onCopy={copyLink} />}
      </section>
      {!apps ? (
        <div style={{ padding: 16, fontSize: 13.5, color: 'var(--text-3)' }}>Loading the applications…</div>
      ) : !shown.length ? (
        <div style={{ ...shell, padding: '4px 12px 14px' }}>
          {search && search.trim() ? <StateScreen kind="empty" {...noMatchText(search)} />
            : <StateScreen kind="empty" title={region ? `None from ${region}` : 'No applications yet'}
              body={`Hand subs the link ${PUBLIC_URL.replace('https://', '')}: what they send lands here.`} />}
        </div>
      ) : (
        <div ref={pageRef} className="apps-page" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {rail.on && (
            <div style={{ position: 'sticky', top: rail.head + 12, height: `calc(100vh - ${rail.head + 24}px)`, display: 'flex', flex: 'none' }}>
              <ScrollRail page box={null} items={rail.items} title="Sections" width={140} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 26 }}>
            {STATUSES.map(([k, label]) => (
              <section key={k} aria-labelledby={`apps-${k}`}>
                <div id={`apps-${k}`} className="apps-sec">
                  {label}
                  <span style={pill(bySection[k].length ? k : 'declined')}>{bySection[k].length}</span>
                </div>
                {bySection[k].length
                  ? <div className="app-cards">{bySection[k].map(appCard)}</div>
                  : <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>None right now</div>}
              </section>
            ))}
          </div>
        </div>
      )}
      {lift && lifted && (
        <Lift rect={lift.rect} from={lift.from} fill cardStyle={{ background: 'var(--bg-surface-2)' }} ariaLabel={`Application: ${lifted.firm_name}`}
          paused={!!viewing || !!confirmDialogProps.open} onClosed={() => { setLift(null); setDeclining(null); setError(null); }}
          line={head(lifted)}>
          {detail(lifted)}
        </Lift>
      )}
      {viewing && (
        <DocViewer doc={viewing.doc}
          subtitle={`${DOC_NAME[viewing.kind]} · ${viewing.app.firm_name}`}
          fetchBlob={() => fetchSubApplicationFile(viewing.app.id, viewing.kind)}
          onDownload={() => downloadSubApplicationFile(viewing.app.id, viewing.kind, viewing.app.files[viewing.kind].filename)}
          onClose={() => setViewing(null)} />
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
}
